// src/components/CheckoutModal.jsx
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaShoppingBag } from 'react-icons/fa';
import { useState, useEffect, useRef } from 'react';

export default function CheckoutModal({ isOpen, onClose, cartData, totalAmount }) {
  const [iframeUrl, setIframeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (isOpen && cartData) {
      // Build the URL with cart data and theme parameter
      const encodedCart = encodeURIComponent(JSON.stringify(cartData));
      const url = `https://kenitels.directcore.tech/checkout?cart=${encodedCart}&total=${totalAmount}&theme=dark&bg=040404`;
      setIframeUrl(url);
      setIsLoading(true);
    }
  }, [isOpen, cartData, totalAmount]);

  // Close on ESC key only
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    
    // Try to send a postMessage to the iframe
    try {
      const iframe = iframeRef.current;
      if (iframe) {
        // Send theme data to the iframe
        iframe.contentWindow.postMessage({
          type: 'theme',
          data: {
            background: '#040404',
            textColor: '#ffffff',
            primaryColor: '#f97316',
            secondaryColor: '#14b8a6',
            cardBackground: 'rgba(30, 30, 40, 0.4)',
            mutedColor: '#a1a1aa'
          }
        }, 'https://nexcom.onelynq.app');
      }
    } catch (e) {
      console.log('Cannot send postMessage due to cross-origin policy');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="checkout-modal-wrapper">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="checkout-modal-overlay"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 40 }}
            transition={{ 
              type: "spring", 
              damping: 25, 
              stiffness: 300,
              duration: 0.4 
            }}
            className="checkout-modal"
          >
            <div className="checkout-modal-header">
              <div className="checkout-modal-header-left">
                <div className="checkout-modal-icon-wrapper">
                  <FaShoppingBag className="checkout-modal-icon" />
                </div>
                <div>
                  <h2>Complete Your Purchase</h2>
                </div>
              </div>
              <button onClick={onClose} className="checkout-modal-close">
                <FaTimes />
              </button>
            </div>

            <div className="checkout-modal-body">
              {isLoading && (
                <div className="checkout-loader">
                  <div className="checkout-loader-spinner"></div>
                  <p className="checkout-loader-text">Loading secure checkout...</p>
                </div>
              )}

              <iframe
                ref={iframeRef}
                src={iframeUrl}
                className="checkout-iframe"
                title="Nexcom Checkout"
                allow="payment"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                onLoad={handleIframeLoad}
                style={{
                  opacity: isLoading ? 0 : 1,
                  transition: 'opacity 0.5s ease',
                }}
              />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}